import { useEffect, useMemo, useState } from "react";
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";

function EmployeeConverted() {
  const navigate = useNavigate();

  const [employee, setEmployee] =
    useState(null);

  const [clients, setClients] =
    useState([]);

  const [selectedClient, setSelectedClient] =
    useState(null);

  const [planType, setPlanType] =
    useState("LT");

  const [amount, setAmount] =
    useState("");

  const [paymentDate, setPaymentDate] =
    useState(
      new Date()
        .toISOString()
        .split("T")[0]
    );

  const [paymentMode, setPaymentMode] =
    useState("UPI");

  const [message, setMessage] =
    useState("");

  const [saving, setSaving] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  useEffect(() => {
    const savedEmployee =
      localStorage.getItem("crmEmployee");

    if (!savedEmployee) {
      navigate("/employee-login");
      return;
    }

    const parsedEmployee =
      JSON.parse(savedEmployee);

    setEmployee(parsedEmployee);

    const loadConverted = async () => {
      try {
        setLoading(true);

        const convertedQuery = query(
          collection(db, "leads"),
          where(
            "assignedEmployeeEmail",
            "==",
            parsedEmployee.email
              .trim()
              .toLowerCase()
          )
        );

        const snapshot =
          await getDocs(convertedQuery);

        const list = snapshot.docs
          .map((documentItem) => ({
            id: documentItem.id,
            ...documentItem.data(),
          }))
          .filter(
            (lead) =>
              lead.status ===
              "Converted"
          );

        setClients(list);
      } finally {
        setLoading(false);
      }
    };

    loadConverted();
  }, [navigate]);

  const getTimeValue = (value) => {
    if (!value) {
      return 0;
    }

    try {
      if (
        typeof value.toDate === "function"
      ) {
        return value.toDate().getTime();
      }

      const date = new Date(value);

      return Number.isNaN(date.getTime())
        ? 0
        : date.getTime();
    } catch {
      return 0;
    }
  };

  const sortedClients = useMemo(() => {
    return [...clients].sort(
      (a, b) => {
        const aWorked =
          getTimeValue(
            a.lastPaymentUpdatedAt ||
              a.updatedAt
          );

        const bWorked =
          getTimeValue(
            b.lastPaymentUpdatedAt ||
              b.updatedAt
          );

        if (!aWorked && bWorked) {
          return -1;
        }

        if (aWorked && !bWorked) {
          return 1;
        }

        if (!aWorked && !bWorked) {
          return (
            getTimeValue(b.createdAt) -
            getTimeValue(a.createdAt)
          );
        }

        return aWorked - bWorked;
      }
    );
  }, [clients]);

  const getPayments = (client) =>
    Array.isArray(client.paymentHistory)
      ? client.paymentHistory
      : [];

  const totals = (client) => {
    const payments =
      getPayments(client);

    const calculate = (type) =>
      payments
        .filter(
          (payment) =>
            payment.planType === type
        )
        .reduce(
          (total, payment) =>
            total +
            Number(payment.amount || 0),
          0
        );

    const lt = calculate("LT");
    const mt = calculate("MT");
    const st = calculate("ST");

    return {
      lt,
      mt,
      st,
      total: lt + mt + st,
    };
  };

  const monthlyTotal = useMemo(() => {
    const currentMonth =
      new Date()
        .toISOString()
        .slice(0, 7);

    return clients.reduce(
      (overallTotal, client) => {
        const payments =
          getPayments(client);

        const monthAmount =
          payments
            .filter((payment) =>
              (
                payment.paymentDate ||
                ""
              ).startsWith(
                currentMonth
              )
            )
            .reduce(
              (sum, payment) =>
                sum +
                Number(
                  payment.amount || 0
                ),
              0
            );

        return (
          overallTotal +
          monthAmount
        );
      },
      0
    );
  }, [clients]);

  const addPayment = async () => {
    if (!selectedClient) {
      return;
    }

    if (
      !amount ||
      Number(amount) <= 0
    ) {
      setMessage(
        "Enter a valid amount."
      );
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      const activityTime =
        new Date().toISOString();

      const paymentEntry = {
        planType,
        amount: Number(amount),
        paymentDate,
        paymentMode,
        employeeId:
          employee.id,
        employeeName:
          employee.name,
        createdAt:
          activityTime,
      };

      await updateDoc(
        doc(
          db,
          "leads",
          selectedClient.id
        ),
        {
          paymentHistory:
            arrayUnion(
              paymentEntry
            ),

          lastPaymentUpdatedAt:
            activityTime,

          updatedAt:
            serverTimestamp(),
        }
      );

      const updatedClient = {
        ...selectedClient,

        paymentHistory: [
          ...getPayments(
            selectedClient
          ),
          paymentEntry,
        ],

        lastPaymentUpdatedAt:
          activityTime,

        updatedAt:
          activityTime,
      };

      setClients((current) =>
        current.map((client) =>
          client.id ===
          selectedClient.id
            ? updatedClient
            : client
        )
      );

      setSelectedClient(
        updatedClient
      );

      setAmount("");

      setMessage(
        "Payment added successfully."
      );
    } catch (error) {
      console.error(
        "Payment save error:",
        error
      );

      setMessage(
        "Unable to add payment."
      );
    } finally {
      setSaving(false);
    }
  };

  if (selectedClient) {
    const clientTotals =
      totals(selectedClient);

    return (
      <div className="employee-calls-page">
        <header className="mobile-crm-header">
          <div>
            <h1>Trading Wealth CRM</h1>
            <p>Converted Customer</p>
          </div>

          <button
            className="mobile-back-button"
            onClick={() =>
              setSelectedClient(null)
            }
          >
            Back
          </button>
        </header>

        <main className="employee-calls-main">
          {message && (
            <div className="attendance-message">
              {message}
            </div>
          )}

          <section className="customer-call-card">
            <div>
              <h2>
                {
                  selectedClient.customerName
                }
              </h2>

              <p>
                {selectedClient.phone}
              </p>
            </div>
          </section>

          <section className="dashboard-stats">
            <div className="dashboard-stat-card">
              <p>LT Total</p>
              <h3>
                ₹
                {clientTotals.lt.toLocaleString(
                  "en-IN"
                )}
              </h3>
            </div>

            <div className="dashboard-stat-card">
              <p>MT Total</p>
              <h3>
                ₹
                {clientTotals.mt.toLocaleString(
                  "en-IN"
                )}
              </h3>
            </div>

            <div className="dashboard-stat-card">
              <p>ST Total</p>
              <h3>
                ₹
                {clientTotals.st.toLocaleString(
                  "en-IN"
                )}
              </h3>
            </div>

            <div className="dashboard-stat-card">
              <p>Overall</p>
              <h3>
                ₹
                {clientTotals.total.toLocaleString(
                  "en-IN"
                )}
              </h3>
            </div>
          </section>

          <section className="employee-update-card">
            <h3>Add Payment</h3>

            <div className="form-group">
              <label>Plan</label>

              <select
                value={planType}
                onChange={(event) =>
                  setPlanType(
                    event.target.value
                  )
                }
              >
                <option value="LT">
                  LT
                </option>

                <option value="MT">
                  MT
                </option>

                <option value="ST">
                  ST
                </option>
              </select>
            </div>

            <div className="form-group">
              <label>Amount Received</label>

              <input
                type="number"
                min="1"
                value={amount}
                onChange={(event) =>
                  setAmount(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="form-group">
              <label>Payment Date</label>

              <input
                type="date"
                value={paymentDate}
                onChange={(event) =>
                  setPaymentDate(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="form-group">
              <label>Payment Mode</label>

              <select
                value={paymentMode}
                onChange={(event) =>
                  setPaymentMode(
                    event.target.value
                  )
                }
              >
                <option value="UPI">
                  UPI
                </option>

                <option value="Bank Transfer">
                  Bank Transfer
                </option>

                <option value="Card">
                  Card
                </option>

                <option value="Cash">
                  Cash
                </option>

                <option value="Other">
                  Other
                </option>
              </select>
            </div>

            <button
              className="save-call-update-button"
              onClick={addPayment}
              disabled={saving}
            >
              {saving
                ? "Saving..."
                : "Add Payment"}
            </button>
          </section>

          <section className="remark-history-card">
            <h3>Payment History</h3>

            {getPayments(
              selectedClient
            ).length === 0 ? (
              <p className="no-remarks-text">
                No payment entries yet.
              </p>
            ) : (
              <div className="remark-timeline">
                {[
                  ...getPayments(
                    selectedClient
                  ),
                ]
                  .reverse()
                  .map(
                    (
                      payment,
                      index
                    ) => (
                      <div
                        className="remark-history-item"
                        key={index}
                      >
                        <strong>
                          {
                            payment.planType
                          }{" "}
                          – ₹
                          {Number(
                            payment.amount
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </strong>

                        <p>
                          {
                            payment.paymentDate
                          }{" "}
                          •{" "}
                          {
                            payment.paymentMode
                          }
                        </p>
                      </div>
                    )
                  )}
              </div>
            )}
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="employee-calls-page">
      <header className="mobile-crm-header">
        <div>
          <h1>Trading Wealth CRM</h1>
          <p>Converted Clients</p>
        </div>

        <button
          className="mobile-back-button"
          onClick={() =>
            navigate("/employee-calls")
          }
        >
          My Calls
        </button>
      </header>

      <main className="employee-calls-main">
        <section className="employee-calls-heading">
          <p className="small-label">
            MONTHLY COLLECTION
          </p>

          <h2>
            ₹
            {monthlyTotal.toLocaleString(
              "en-IN"
            )}
          </h2>

          <p>
            Total amount received by{" "}
            {employee?.name ||
              "employee"}{" "}
            this month.
          </p>
        </section>

        {loading ? (
          <div className="employee-empty-state">
            Loading customers...
          </div>
        ) : sortedClients.length === 0 ? (
          <div className="employee-empty-state">
            No converted customers yet.
          </div>
        ) : (
          <div className="mobile-lead-list">
            {sortedClients.map((client) => {
              const clientTotals =
                totals(client);

              return (
                <article
                  key={client.id}
                  className="mobile-lead-card"
                >
                  <h3>
                    {
                      client.customerName
                    }
                  </h3>

                  <p>{client.phone}</p>

                  <div className="mobile-lead-meta">
                    <span>
                      LT ₹
                      {clientTotals.lt.toLocaleString(
                        "en-IN"
                      )}
                    </span>

                    <span>
                      MT ₹
                      {clientTotals.mt.toLocaleString(
                        "en-IN"
                      )}
                    </span>

                    <span>
                      ST ₹
                      {clientTotals.st.toLocaleString(
                        "en-IN"
                      )}
                    </span>

                    <span>
                      Total ₹
                      {clientTotals.total.toLocaleString(
                        "en-IN"
                      )}
                    </span>
                  </div>

                  <button
                    className="open-lead-button"
                    style={{
                      width: "100%",
                      marginTop: "12px",
                    }}
                    onClick={() =>
                      setSelectedClient(
                        client
                      )
                    }
                  >
                    Open Payment Record
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default EmployeeConverted;